import React, { useReducer, useEffect, type Dispatch } from "react";
import { RouterProvider } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { AuthenticationRouter, JoinRouter } from "./Routes";
import { AuthenticationContext, AuthenticationDispatchContext } from "./AuthenticationContext";
import {
    AuthenticationConfigurationContext,
    defaultCoordinatorLoginConfiguration,
    reducer as configurationReducer,
} from "./Configuration";
import { defaultState, reducer } from "./Store";
import { Cookies, LoginLink } from "../types";
import { AuthenticationAction, AuthenticationViews } from "./types";
import { makeUrl } from "../../../api/makeUrl";

function fallbackRender(props: { error?: Error }) {
    // Call resetErrorBoundary() to reset the error boundary and retry the render.

    return (
        <div role="alert">
            <p>Something went wrong:</p>
            <pre style={{ color: "red" }}>{props.error?.message}</pre>
        </div>
    );
}

export interface AuthenticationProps {
    cookies: Cookies;
    lastSessionCookie: string;
    lastIdpLink?: LoginLink;
    redirectUrl: string;
    reloginState: string;
    sessionCookieName: string;
    signInId: string;
    hasMfaConfig: boolean;
    allowSnooze2FA?: boolean;
    isTotpEnabled?: boolean;
    totpKey?: string;
    totpQrCodeUri?: string;
    totpAllowRememberMe?: boolean;
    totpRememberMeHours?: number;
    activeView?: string;
    passkeyServerBaseUrl?: string;
    hasRecoveryCodes?: boolean;
}

const App: React.FC<AuthenticationProps> = (props) => {
    if ((props.activeView as AuthenticationViews) && !window.location.hash) {
        window.location.hash = props.activeView!;
    }

    const [state, dispatch] = useReducer(reducer, {
        ...defaultState,
        activeView: props.activeView ? AuthenticationViews.login : defaultState.activeView,
        lastSessionCookie: props.lastSessionCookie,
        redirectUrl: props.redirectUrl || location.href.split("#")[0],
        signInId: props.signInId,
        isTotpEnabled: props.isTotpEnabled,
        totpKey: props.totpKey,
        totpQrCodeUri: props.totpQrCodeUri,
    });
    const [configurationState] = useReducer(configurationReducer, {
        ...defaultCoordinatorLoginConfiguration,
        cookies: props.cookies,
        reloginState: props.reloginState,
        sessionCookieName: props.sessionCookieName,
        lastIdpLink: props.lastIdpLink,
        hasMfaConfig: props.hasMfaConfig,
        allowSnooze2FA: props.allowSnooze2FA ?? false,
        totpAllowRememberMe: props.totpAllowRememberMe ?? false,
        totpRememberMeHours: props.totpRememberMeHours,
        passkeyServerBaseUrl: makeUrl(props.passkeyServerBaseUrl ?? ""),
        hasRecoveryCodes: props.hasRecoveryCodes ?? false,
    });

    const router = location.href.includes("/identityserver/")
        ? AuthenticationRouter
        : JoinRouter;

    return (
        <ErrorBoundary fallbackRender={fallbackRender}>
            <AuthenticationConfigurationContext.Provider value={configurationState}>
                <AuthenticationContext.Provider value={state}>
                    <AuthenticationDispatchContext.Provider value={dispatch as Dispatch<AuthenticationAction>}>
                        <RouterProvider router={router} />
                    </AuthenticationDispatchContext.Provider>
                </AuthenticationContext.Provider>
            </AuthenticationConfigurationContext.Provider>
        </ErrorBoundary>
    );
};

export default App;
